import React from "react";
import { Modal, Table } from "react-bootstrap";

interface Order {
  order: string;
  order_details: string;
  item_name: string;
  unit: number;
  total: string;
  status: string;
  commission_name: string | null;
  uom: string | null;
  rate: string;
  store: string;
  percentage_amount: string | null;
}

interface TransactionData {
  status: boolean;
  error: string | null;
  message: string;
  order_list: Order[];
  total_amount: number;
  total_amount_calculated: number;
  vat_rate: number;
  vat_amount: number;
  total_commission_amount: number;
}

interface TableColumn {
  key: string;
  label: string;
  render?: (value: any, order: Order) => React.ReactNode;
}

interface TransactionModalProps {
  show: boolean;
  onHide: () => void;
  title: string;
  description: string;
  transactionData: TransactionData | null;
  columns: TableColumn[];
  showVAT?: boolean;
  showCommission?: boolean;
  totalLabel?: string;
  size?: "sm" | "lg" | "xl";
}

const TransactionModal: React.FC<TransactionModalProps> = React.memo(({
  show,
  onHide,
  title,
  description,
  transactionData,
  columns,
  showVAT = false,
  showCommission = false,
  totalLabel = "Total",
  size = "lg",
}) => {
  // Memoize the table rows to prevent re-rendering
  const tableRows = React.useMemo(() => {
    if (!transactionData || !transactionData.order_list || transactionData.order_list.length === 0) {
      return (
        <tr>
          <td colSpan={columns.length} style={{ textAlign: "center" }}>
            No transaction data available.
          </td>
        </tr>
      );
    }

    return transactionData.order_list.map((order, index) => (
      <tr key={index}>
        {columns.map((column) => (
          <td key={column.key}>
            {column.render
              ? column.render(
                  order[column.key as keyof Order],
                  order
                )
              : order[column.key as keyof Order] || "N/A"}
          </td>
        ))}
      </tr>
    ));
  }, [transactionData, columns]);

  // Memoize the table headers
  const tableHeaders = React.useMemo(() => (
    <tr>
      {columns.map((column) => (
        <th key={column.key}>{column.label}</th>
      ))}
    </tr>
  ), [columns]);

  // Memoize the footer rows
  const footerRows = React.useMemo(() => (
    <>
      {/* Total row */}
      <tr>
        <td colSpan={columns.length - 1}>
          <strong>{totalLabel}</strong>
        </td>
        <td>
          <strong>
            {transactionData ? transactionData.total_amount : ""}
          </strong>
        </td>
      </tr>

      {/* VAT row */}
      {showVAT && transactionData && (
        <tr>
          <td colSpan={columns.length - 1}>
            <strong>VAT: {transactionData.vat_rate || 0}%</strong>
          </td>
          <td>
            <strong>{transactionData.vat_amount || 0}</strong>
          </td>
        </tr>
      )}

      {/* Total with commission */}
      {showCommission && transactionData && (
        <tr>
          <td colSpan={columns.length - 1}>
            <strong>Total {totalLabel}</strong>
          </td>
          <td>
            <strong>
              {transactionData.total_commission_amount || 0}
            </strong>
          </td>
        </tr>
      )}
    </>
  ), [columns.length, totalLabel, transactionData, showVAT, showCommission]);

  return (
    <Modal
      show={show}
      onHide={onHide}
      className="sellerModal"
      centered
      size={size}
    >
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p>{description}</p>
        <div className="table-responsive">
          <Table striped bordered hover>
            <thead>
              {tableHeaders}
            </thead>
            <tbody>
              {tableRows}
              {footerRows}
            </tbody>
          </Table>
        </div>
      </Modal.Body>
    </Modal>
  );
}, (prevProps, nextProps) => {
  // Custom comparison function for better memoization
  return (
    prevProps.show === nextProps.show &&
    prevProps.title === nextProps.title &&
    prevProps.description === nextProps.description &&
    prevProps.showVAT === nextProps.showVAT &&
    prevProps.showCommission === nextProps.showCommission &&
    prevProps.totalLabel === nextProps.totalLabel &&
    prevProps.size === nextProps.size &&
    prevProps.columns === nextProps.columns &&
    prevProps.transactionData === nextProps.transactionData &&
    prevProps.onHide === nextProps.onHide
  );
});

export default TransactionModal;
